import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft, ArrowRight, Check, ChevronDown, SlidersHorizontal, Star, Trash2,
} from 'lucide-react';

import {
  loadProductImagePlaces, orderProductImages, removeProductImage, setProductMainImage, updateProductImage,
} from './api';
import { SearchField, XomicsSelect } from './parts';
import { errorMessage } from './ui';
import { tx } from './i18n';
import { AttributeSwatch } from './ProductOptions';
import {
  ImagePicker, MAX_PRODUCT_IMAGES, uploadSelectedImages, useSelectedImages,
} from './ProductImageUpload';

// The Images tab: every photo of a product, where the storefront's pages show
// it (its spots: the gallery, the timeline, the closing shot), which choice it
// shows (the Gold ring's photos, the Ember strap's), its place in the order,
// and new photos uploaded straight into the spot and choice being looked at.
// Storefronts read the spots and links from each image (Modules/ProductImages.js).

function hasLook(value) {
  const attributes = value.attributes ?? {};
  return typeof attributes.swatch === 'string' || typeof attributes.texture === 'string'
    || attributes.tones !== undefined;
}

// The options a product's photos follow: the choices that change how it looks
// (a finish, a colour). A product whose choices have no look follows its first.
export function photoOptionsOf(document) {
  const choices = (document?.options ?? [])
    .filter((option) => option.isActive !== false && option.kind === 'choice')
    .map((option) => ({ ...option, values: (option.values ?? []).filter((value) => value.isActive !== false) }))
    .filter((option) => option.values.length > 0);
  const looks = choices.filter((option) => option.values.some(hasLook));
  return looks.length > 0 ? looks : choices.slice(0, 1);
}

function valueLabel(value) {
  const group = typeof value.attributes?.groupName === 'string' ? value.attributes.groupName.trim() : '';
  return group ? `${group} ${value.name}` : value.name;
}

function linkOf(image, optionCode) {
  return (image.optionValues ?? []).find((link) => link.option === optionCode)?.value ?? '';
}

function photoUrl(image) {
  return image.variants?.card?.url ?? image.variants?.detail?.url ?? image.url;
}

// The spots are the same for every product; read once per visit.
let placesOnce = null;
function usePlaces() {
  const [places, setPlaces] = useState([]);
  useEffect(() => {
    let live = true;
    placesOnce = placesOnce ?? loadProductImagePlaces().catch((error) => { placesOnce = null; throw error; });
    placesOnce.then((list) => { if (live) setPlaces(list); }).catch(() => {});
    return () => { live = false; };
  }, []);
  return places;
}

function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const closeOnPointer = (event) => { if (!ref.current?.contains(event.target)) setOpen(false); };
    const closeOnEscape = (event) => { if (event.key === 'Escape') { event.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', closeOnPointer);
    document.addEventListener('keydown', closeOnEscape, true);
    return () => {
      document.removeEventListener('mousedown', closeOnPointer);
      document.removeEventListener('keydown', closeOnEscape, true);
    };
  }, [open]);
  return [open, setOpen, ref];
}

// Which photos are in view, by spot and by choice. The same menu as the
// products page's filter, in two sections.
function PhotoFilter({ sections }) {
  const [open, setOpen, ref] = usePopover();
  const chosen = sections.map((section) => section.entries.find((entry) => entry.value === section.value) ?? section.entries[0]);
  const filtered = chosen.filter((entry, index) => entry !== sections[index].entries[0]);
  const label = filtered.length ? filtered.map((entry) => entry.label).join(' · ') : tx('ui.products.images.all');
  const count = filtered.length ? filtered[filtered.length - 1].count : sections[0]?.entries[0]?.count;
  return (
    <div className="xo-product-filter-control" ref={ref}>
      <button type="button" className={`xo-product-filter-button${filtered.length ? ' is-filtered' : ''}`}
        aria-haspopup="menu" aria-expanded={open} aria-label={tx('ui.products.images.show')}
        onClick={() => setOpen((current) => !current)}>
        <SlidersHorizontal aria-hidden="true" />
        <span>{label}</span><b>{count}</b>
        <ChevronDown aria-hidden="true" />
      </button>
      {open && (
        <div className="xo-product-filter-menu xo-pm-photo-menu" role="menu">
          {sections.map((section) => (
            <React.Fragment key={section.key}>
              <p className="xo-pm-photo-menu-head">{section.title}</p>
              {section.entries.map((entry) => (
                <button type="button" role="menuitemradio" key={entry.value} aria-checked={section.value === entry.value}
                  className={section.value === entry.value ? 'is-active' : ''}
                  onClick={() => { section.onPick(entry.value); setOpen(false); }}>
                  <span className="xo-pm-photo-menu-label">
                    {entry.attributes && <AttributeSwatch attributes={entry.attributes} />}
                    <span>{entry.label}</span>
                  </span>
                  <b>{entry.count}</b>
                  {section.value === entry.value ? <Check aria-hidden="true" /> : <span />}
                </button>
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

// The spots a photo is shown in: tick as many as it fills. Each tick saves.
function ShownIn({ places, value, disabled, onChange }) {
  const [open, setOpen, ref] = usePopover();
  const names = places.filter((place) => value.includes(place.code)).map((place) => place.name);
  return (
    <div className="xo-pm-shown-in" ref={ref}>
      <button type="button" className={`xo-pm-shown-in-button${names.length ? '' : ' is-nowhere'}`}
        aria-haspopup="menu" aria-expanded={open} disabled={disabled}
        onClick={() => setOpen((current) => !current)}>
        <span>{names.length ? names.join(', ') : tx('ui.products.images.nowhere')}</span>
        <ChevronDown aria-hidden="true" />
      </button>
      {open && (
        <div className="xo-product-filter-menu xo-pm-shown-in-menu" role="menu">
          {places.map((place) => {
            const on = value.includes(place.code);
            return (
              <button type="button" role="menuitemcheckbox" key={place.code} aria-checked={on}
                className={on ? 'is-active' : ''} disabled={disabled}
                onClick={() => onChange(on ? value.filter((code) => code !== place.code) : [...value, place.code])}>
                <span className={`xo-pm-tick${on ? ' is-on' : ''}`} aria-hidden="true">{on && <Check />}</span>
                <span>{place.name}</span>
                <span />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ProductImageManager({
  product, photoOptions, mayManage, filter, onFilter, onSaved, onOpen,
}) {
  const places = usePlaces();
  const [newImages, setNewImages] = useSelectedImages();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  // 'all', 'none' (not on the website) or a place code.
  const [spot, setSpot] = useState('all');
  const images = product.images;
  const primary = photoOptions[0] ?? null;
  const target = primary && filter !== 'all' && filter !== 'every'
    ? primary.values.find((value) => value.code === filter) ?? null : null;
  const spotPlace = places.find((place) => place.code === spot) ?? null;
  const needle = query.trim().toLowerCase();
  const placeNames = (image) => (image.places ?? []).map((code) => places.find((place) => place.code === code)?.name ?? code);
  // What a search reads: the description, the spots and the choices a photo shows.
  const searchText = (image) => [image.altText ?? '', ...placeNames(image), ...(image.optionValues ?? []).map((link) => {
    const option = photoOptions.find((entry) => entry.code === link.option);
    const value = option?.values.find((entry) => entry.code === link.value);
    return value ? valueLabel(value) : link.value;
  })].join(' ').toLowerCase();
  const inSpot = (image) => (spot === 'all' ? true : spot === 'none'
    ? (image.places ?? []).length === 0 : (image.places ?? []).includes(spot));
  const inChoice = (image) => (primary === null || filter === 'all' ? true : filter === 'every'
    ? (image.optionValues ?? []).length === 0 : linkOf(image, primary.code) === filter);
  const shown = images.filter((image) => (needle === '' || searchText(image).includes(needle)) && inSpot(image) && inChoice(image));
  const room = MAX_PRODUCT_IMAGES - images.length;
  const every = (option) => tx('ui.products.images.every_value', { option: option.name.toLowerCase() });

  async function act(work, message) {
    setBusy(true);
    setError('');
    try {
      await work();
      onSaved(message);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  function link(image, optionCode, valueCode) {
    // Links to options this tab does not show stay as they are.
    const links = (image.optionValues ?? []).filter((entry) => entry.option !== optionCode);
    if (valueCode !== '') links.push({ option: optionCode, value: valueCode });
    act(() => updateProductImage(product.id, image.id, { optionValues: links }), tx('ui.products.images.saved'));
  }

  function place(image, codes) {
    act(() => updateProductImage(product.id, image.id, { places: codes }), tx('ui.products.images.saved'));
  }

  function describe(image, text) {
    if (text.trim() === (image.altText ?? '').trim()) return;
    act(() => updateProductImage(product.id, image.id, { altText: text }), tx('ui.products.images.saved'));
  }

  // One place earlier or later among the photos in view; the rest keep their places.
  function move(image, step) {
    const visible = shown.map((entry) => entry.id);
    const neighbour = visible[visible.indexOf(image.id) + step];
    if (neighbour === undefined) return;
    const ids = images.map((entry) => entry.id);
    const from = ids.indexOf(image.id);
    const to = ids.indexOf(neighbour);
    [ids[from], ids[to]] = [ids[to], ids[from]];
    act(() => orderProductImages(product.id, ids), tx('ui.products.images.ordered'));
  }

  function remove(image) {
    if (!window.confirm(tx('ui.products.images.remove_confirm', { product: product.name }))) return;
    act(() => removeProductImage(product.id, image.id), tx('ui.products.images.removed'));
  }

  function makeMain(image) {
    act(() => setProductMainImage(product.id, image.id),
      tx("ui.products.code_main_image_updated_5af768ee", { code: product.name }));
  }

  // New photos go in the spot and choice in view; with none in view, the gallery.
  async function upload() {
    const links = target ? [{ option: primary.code, value: target.code }] : undefined;
    const codes = spotPlace ? [spotPlace.code] : ['gallery'];
    setBusy(true);
    setError('');
    try {
      await uploadSelectedImages(product.id, product.name, newImages, setProgress, links, codes);
      const added = newImages.length;
      newImages.forEach((image) => URL.revokeObjectURL(image.previewUrl));
      setNewImages([]);
      onSaved(tx("ui.products.length_imagevalue_added_to_code_2fbcdd5c", { length: added, value: added === 1 ? '' : 's', code: product.name }));
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
      setProgress('');
    }
  }

  let uploadHeading = tx("ui.products.add_images_49de5b8d");
  if (target) uploadHeading = tx('ui.products.images.add_for', { value: valueLabel(target) });
  else if (primary) uploadHeading = tx('ui.products.images.add_every', { option: primary.name.toLowerCase() });
  if (spotPlace) uploadHeading = `${uploadHeading} · ${spotPlace.name}`;

  const sections = [{
    key: 'spot',
    title: tx('ui.products.images.spot'),
    value: spot,
    onPick: setSpot,
    entries: [
      { value: 'all', label: tx('ui.products.images.all_spots'), count: images.length },
      { value: 'none', label: tx('ui.products.images.nowhere'), count: images.filter((image) => (image.places ?? []).length === 0).length },
      ...places.map((entry) => ({
        value: entry.code, label: entry.name, count: images.filter((image) => (image.places ?? []).includes(entry.code)).length,
      })).filter((entry) => entry.count > 0 || entry.value === spot),
    ],
  }];
  if (primary) {
    sections.push({
      key: 'choice',
      title: primary.name,
      value: filter,
      onPick: onFilter,
      entries: [
        { value: 'all', label: tx('ui.products.images.all'), count: images.length },
        { value: 'every', label: every(primary), count: images.filter((image) => (image.optionValues ?? []).length === 0).length },
        ...primary.values.map((value) => ({
          value: value.code,
          label: valueLabel(value),
          count: images.filter((image) => linkOf(image, primary.code) === value.code).length,
          attributes: hasLook(value) ? value.attributes : null,
        })),
      ],
    });
  }

  return (
    <div className="xo-pm-images">
      <div className="xo-pm-section-head">
        <div>
          <h3>{tx("ui.products.images_09e871c9")}</h3>
          <p>{primary
            ? tx('ui.products.images.hint_linked', { option: primary.name.toLowerCase() })
            : tx("ui.products.originals_become_thumbnail_card_and_detail_sizes_3aa93d1e")}</p>
        </div>
      </div>

      {images.length > 0 && (
        <div className="xo-pm-photo-toolbar">
          <SearchField className="xo-pm-photo-search" value={query} onChange={setQuery}
            placeholder={tx('ui.products.images.search')} />
          <PhotoFilter sections={sections} />
        </div>
      )}

      {error && <p className="xo-alert t-bad" role="alert">{error}</p>}

      {shown.length === 0 ? (
        <p className="xo-pm-photo-empty">
          {needle !== '' || spot !== 'all' ? tx("ui.products.nothing_matches_17e7aebf")
            : target ? tx('ui.products.images.none_for', { value: valueLabel(target) }) : tx('ui.products.detail.no_images')}
        </p>
      ) : (
        <div className="xo-pm-photo-grid">
          {shown.map((image, index) => (
            <figure key={image.id} className={`xo-pm-photo${image.isMain ? ' is-main' : ''}`}>
              <button type="button" className="xo-pm-photo-view" aria-label={image.altText || product.name}
                onClick={() => onOpen(image.id)}>
                <img src={photoUrl(image)} alt="" loading="lazy" decoding="async" />
                {image.isMain && <span className="xo-pm-main-badge"><Star aria-hidden="true" />{tx("ui.products.main_image_a5fcb218")}</span>}
              </button>
              <figcaption>
                <div className="xo-pm-photo-field">
                  <span>{tx('ui.products.images.shown_in')}</span>
                  {mayManage ? (
                    <ShownIn places={places} value={image.places ?? []} disabled={busy}
                      onChange={(codes) => place(image, codes)} />
                  ) : (
                    <p className="xo-pm-photo-link">{placeNames(image).join(', ') || tx('ui.products.images.nowhere')}</p>
                  )}
                </div>
                {photoOptions.map((option) => (mayManage ? (
                  <label key={option.code} className="xo-pm-photo-field">
                    <span>{option.name}</span>
                    <XomicsSelect value={linkOf(image, option.code)} disabled={busy}
                      aria-label={tx('ui.products.images.shows_option', { option: option.name })}
                      onChange={(event) => link(image, option.code, event.target.value)}>
                      <option value="">{every(option)}</option>
                      {option.values.map((value) => <option key={value.code} value={value.code}>{valueLabel(value)}</option>)}
                    </XomicsSelect>
                  </label>
                ) : (
                  <p key={option.code} className="xo-pm-photo-link">
                    {option.name}: {valueLabel(option.values.find((value) => value.code === linkOf(image, option.code)) ?? { name: every(option) })}
                  </p>
                )))}
                {mayManage && (
                  <>
                    <label className="xo-pm-photo-field">
                      <span>{tx('ui.products.images.description')}</span>
                      <input key={`${image.id}:${image.altText ?? ''}`} defaultValue={image.altText ?? ''}
                        placeholder={product.name} maxLength={255} disabled={busy}
                        onBlur={(event) => describe(image, event.target.value)}
                        onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }} />
                    </label>
                    <div className="xo-pm-photo-tools">
                      {image.isMain ? <span /> : (
                        <button type="button" className="xo-link" disabled={busy} onClick={() => makeMain(image)}>
                          <Star aria-hidden="true" />{tx("ui.products.make_main_13c815c2")}
                        </button>
                      )}
                      <span>
                        <button type="button" className="xo-icon-btn" disabled={busy || index === 0}
                          aria-label={tx('ui.products.images.move_earlier')} onClick={() => move(image, -1)}>
                          <ArrowLeft aria-hidden="true" />
                        </button>
                        <button type="button" className="xo-icon-btn" disabled={busy || index === shown.length - 1}
                          aria-label={tx('ui.products.images.move_later')} onClick={() => move(image, 1)}>
                          <ArrowRight aria-hidden="true" />
                        </button>
                        <button type="button" className="xo-icon-btn is-danger" disabled={busy}
                          aria-label={tx('ui.products.images.remove')} onClick={() => remove(image)}>
                          <Trash2 aria-hidden="true" />
                        </button>
                      </span>
                    </div>
                  </>
                )}
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {mayManage && (
        <section className="xo-pm-card xo-pm-photo-upload">
          <h3>{uploadHeading}</h3>
          {room <= 0 ? (
            <p className="xo-muted">{tx("ui.products.this_product_already_has_the_maximum_of_max_product_im_a1858db7", { MAX_PRODUCT_IMAGES })}</p>
          ) : (
            <ImagePicker compact images={newImages} setImages={setNewImages} disabled={busy} limit={room}
              chooseMain={images.length === 0} onError={setError} />
          )}
          {newImages.length > 0 && (
            <button type="button" className="xo-btn xo-pm-upload" disabled={busy} onClick={upload}>
              {busy ? progress || tx("ui.products.uploading_d921a79a") : tx("ui.products.upload_selected_images_aa5e5b9f")}
            </button>
          )}
        </section>
      )}
    </div>
  );
}
